import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Card,
  Paper,
  Tabs,
  Tab,
  Typography,
  Checkbox,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MailIcon from "@mui/icons-material/Mail";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AlertItem from "./AlertItem";
import CustomDateRangePicker from "./DateRangeSelector";
import ClearIcon from "@mui/icons-material/Clear";
import {
  deleteAlertById,
  updateAlert,
  toggleAlertStar,
  getStarredAlerts,
  getAlertCounts,
  fetchAlertsPage,
} from "./AlertsApis";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import VehicleIcon from "../../images/alerts/Vehicle.svg";
import UsersIcon from "../../images/alerts/Users.svg";
import ExpiryIcon from "../../images/alerts/Expiry.svg";
import { formatDate } from "../../utils/formatDate";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Envelop from "../../images/alerts/Envelop.svg";
import deleteIcon from "../../images/alerts/deleteSvg.svg";
import sortingArrow1 from "../../images/sortingArrow1.svg";
import sortingArrow2 from "../../images/sortingArrow2.svg";
import config from "../../config.js";
import axios from "axios";
import { Tooltip } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Menu } from "@mui/material";

import { useUser } from "../../pages/login/UserContextPersistent";
import { useUserInfo } from "../../hooks/useUserInfo";
import {
  ALERTS_DATA_CATEGORY_BY_TAB_KEY,
  computeAlertMetric,
  getBulkActionLabel,
  getBulkActionsForWidget,
  getLegacyAlertsFilters,
  getLegacyAlertsListHeader,
  getLegacyAlertsTabs,
  hasAlertDataPermission,
} from "../../config/alertsModuleCatalog";
import {
  buildAlertsFiltersFromLayout,
  buildAlertsListHeaderFromLayout,
  buildAlertsTabsFromLayout,
} from "../../layout/alertsLayoutUtils";
const PAGE_SIZE = 50;
const AUTO_FETCH_INTERVAL = 5 * 60 * 1000;

const emptyAlertStore = () => ({
  all: [],
  user: [],
  vehicle: [],
  expiry: [],
});

const readCategoryAlerts = (store, category) => {
  if (category === "all") return store.all;
  if (category === "user") return store.user;
  if (category === "vehicle") return store.vehicle;
  if (category === "expiry") return store.expiry;
  return [];
};

const getAlertDateBounds = (startDate, endDate) => {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getAlertDateQueryRange = (startDate, endDate) => {
  const { start, end } = getAlertDateBounds(startDate, endDate);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
};

const mergeAlertsById = (currentAlerts, incomingAlerts) => {
  const seen = new Set(currentAlerts.map((alert) => alert.id));
  const appended = incomingAlerts.filter((alert) => !seen.has(alert.id));
  return [...currentAlerts, ...appended];
};

const withStarState = (alert, starredIds) => ({
  ...alert,
  isRead: alert.statusFlag === "READ",
  isStarred: starredIds.has(alert.id) || alert.isStarred === true,
});

const StyledTabs = styled(Tabs)(({ theme }) => ({
  background: "#E8EAEE",
  boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
  borderRadius: "8px",
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 100,
    padding: "6px 16px",
  },
  "& .Mui-selected": {
    backgroundColor: "white",
    boxShadow: "rgba(0, 0, 0, 0.24) 4px 3px 8px",
    // borderRadius: "8px 8px 0px 0px",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#20A8E0",
    height: "3px",
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiInputLabel-outlined": {
    transform: "translate(16px, 8px) scale(1)",
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(0.75)",
    backgroundColor: "#F2F2F3",
    padding: "0 4px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "none",
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "none",
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "none",
    border: "none",
  },
  height: "35px",
  borderRadius: "4px",
  backgroundColor: "#F2F2F3",
  border: "1px solid #414958",
}));

const StyledSortSelect = styled(Select)(({ theme }) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  height: "32px",
  minWidth: "40px",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  borderRadius: "4px",
  "& .MuiSelect-select": {
    color: "white",
    fontSize: "0.875rem",
    padding: "6px 8px",
  },
  "& .MuiSelect-icon": {
    color: "white",
  },
}));

const StyledSortDropdown = styled(Select)(({ theme }) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #414958",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#20A8E0",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#20A8E0",
  },
  height: "40px",
  minWidth: "150px",
  backgroundColor: "white",
  borderRadius: "6px",
  "& .MuiSelect-select": {
    color: "#183059",
    fontSize: "0.875rem",
    fontWeight: 500,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  "& .MuiSelect-icon": {
    color: "#183059",
  },
}));

const getAllOrganizationVehicles = async () => {
  try {
    const orgId = JSON.parse(localStorage.getItem("userState"))?.organization
      ?.id;
    if (!orgId) {
      console.error("Organization ID not found");
      return [];
    }
    const response = await axios.get(`${config.baseURL}/vehicle/organization`, {
      params: { organizationId: orgId },
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching organization vehicles:", error);
    return [];
  }
};

const AlertList = ({
  moduleLayout = null,
  searchTerm,
  setSearchTerm,
  selectedTab,
  setSelectedTab,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [userAlerts, setUserAlerts] = useState([]);
  const [vehicleAlerts, setVehicleAlerts] = useState([]);
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [allTabAlerts, setAllTabAlerts] = useState([]);
  const [displayedAlerts, setDisplayedAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sortDirection, setSortDirection] = useState("desc");

  const { state } = useUser();

  const legacyMainTabs = useMemo(
    () =>
      getLegacyAlertsTabs({
        t,
        features: state?.features,
      }),
    [t, state?.features],
  );

  const layoutMainTabs = useMemo(() => {
    if (!moduleLayout) return null;
    return buildAlertsTabsFromLayout(moduleLayout, {
      t,
      features: state?.features,
    });
  }, [moduleLayout, t, state?.features]);

  const mainTabs = layoutMainTabs || legacyMainTabs;

  const legacyFilterWidgets = useMemo(() => getLegacyAlertsFilters(), []);

  const layoutFilterWidgets = useMemo(() => {
    if (!moduleLayout) return null;
    return buildAlertsFiltersFromLayout(moduleLayout);
  }, [moduleLayout]);

  const filterWidgets = layoutFilterWidgets || legacyFilterWidgets;

  const legacyListHeaderWidgets = useMemo(() => getLegacyAlertsListHeader(), []);

  const layoutListHeaderWidgets = useMemo(() => {
    if (!moduleLayout) return null;
    return buildAlertsListHeaderFromLayout(moduleLayout);
  }, [moduleLayout]);

  const listHeaderWidgets = layoutListHeaderWidgets || legacyListHeaderWidgets;

  const listPageSize = useMemo(() => {
    const paginationWidget = listHeaderWidgets.find((widget) => widget.type === "pagination");
    const size = Number(paginationWidget?.pageSize);
    return Number.isFinite(size) && size > 0 ? size : PAGE_SIZE;
  }, [listHeaderWidgets]);

  const getListHeaderWidget = (type) =>
    listHeaderWidgets.find((widget) => widget.type === type);

  const hasListHeaderWidget = (type) => Boolean(getListHeaderWidget(type));

  const currentTabKey = mainTabs[selectedTab]?.tabKey || null;
  const [currentPageStart, setCurrentPageStart] = useState(1);
  const [currentPageEnd, setCurrentPageEnd] = useState(PAGE_SIZE);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);

  // Tab-specific filter states
  const [tabFilterType, setTabFilterType] = useState("all");
  const [tabStartDate, setTabStartDate] = useState(null);
  const [tabEndDate, setTabEndDate] = useState(null);
  const [tabSelectedVehicle, setTabSelectedVehicle] = useState([]);
  const [tabSelectedUser, setTabSelectedUser] = useState("");
  const [tabSelectedStatus, setTabSelectedStatus] = useState("");

  const [vehicleNamesMap, setVehicleNamesMap] = useState({});

  const [persistentSelectedAlerts, setPersistentSelectedAlerts] = useState([]);

  const [organizationVehicles, setOrganizationVehicles] = useState([]);
  const [starredAlerts, setStarredAlerts] = useState([]);

  const [alertCounts, setAlertCounts] = useState({
    total: 0,
    byType: { USER: 0, VEHICLE: 0, EXPIRY: 0 },
  });
  const [serverHasMore, setServerHasMore] = useState(false);

  const { orgId } = useUserInfo();

  const lastAlertRef = useRef(null);
  const observerRef = useRef(null);
  const alertsStoreRef = useRef(emptyAlertStore());
  const paginationRef = useRef({ cursor: null, hasMore: false, loading: false });
  const fetchGenerationRef = useRef(0);

  const isAlertListFiltered = () =>
    tabFilterType === "unread" ||
    tabFilterType === "read" ||
    tabFilterType === "starred" ||
    Boolean(searchTerm?.trim()) ||
    Boolean(tabStartDate && tabEndDate) ||
    tabSelectedVehicle.length > 0 ||
    Boolean(tabSelectedUser) ||
    Boolean(tabSelectedStatus);

  const commitAlertStore = (nextStore) => {
    alertsStoreRef.current = nextStore;
    setAllTabAlerts(nextStore.all);
    setUserAlerts(nextStore.user);
    setVehicleAlerts(nextStore.vehicle);
    setExpiryAlerts(nextStore.expiry);
  };

  const getReportedAlertTotal = () => {
    const loadedCount = getVisibleAlerts().length;
    if (isAlertListFiltered()) return loadedCount;

    const tab = mainTabs[selectedTab];
    if (!tab) return loadedCount;

    const serverTotal = getTabCount(tab);
    return serverTotal > 0 ? serverTotal : loadedCount;
  };

  const getPaginationInfo = () => {
    const loadedCount = getVisibleAlerts().length;
    const totalAlerts = getReportedAlertTotal();
    const totalPages = Math.max(1, Math.ceil(totalAlerts / listPageSize));
    const moreLoaded = loadedCount > currentPage * listPageSize;
    const moreOnServer =
      !isAlertListFiltered() && serverHasMore && loadedCount < totalAlerts;
    const canGoNext =
      currentPage < totalPages && (moreLoaded || moreOnServer);

    if (sortDirection === "desc") {
      // For newest first (desc) - normal pagination
      const startIndex =
        totalAlerts === 0 ? 0 : (currentPage - 1) * listPageSize + 1;
      const endIndex =
        totalAlerts === 0
          ? 0
          : Math.min(currentPage * listPageSize, totalAlerts);
      return {
        startIndex,
        endIndex,
        totalAlerts,
        totalPages,
        canGoNext,
        canGoPrevious: currentPage > 1,
      };
    } else {
      // For oldest first (asc) - countdown display
      // Page 1: shows (totalAlerts-50) - totalAlerts of totalAlerts
      // Page 2: shows (totalAlerts-100) - (totalAlerts-50) of totalAlerts
      // Last page: shows 1 - (remaining items) of totalAlerts

      const itemsShownOnCurrentPage = currentPage * listPageSize;
      const itemsShownOnPreviousPage = (currentPage - 1) * listPageSize;

      // Calculate the countdown display range
      const endIndex =
        totalAlerts === 0 ? 0 : totalAlerts - itemsShownOnPreviousPage;
      const startIndex =
        totalAlerts === 0
          ? 0
          : Math.max(1, totalAlerts - itemsShownOnCurrentPage + 1);

      return {
        startIndex,
        endIndex,
        totalAlerts,
        totalPages,
        canGoNext,
        canGoPrevious: currentPage > 1,
      };
    }
  };

  //Helper function to check permissions
  const hasAlertPermission = (alertType) =>
    hasAlertDataPermission(alertType, state?.features);

  const getVehicleById = async (vehicleId, organizationId) => {
    //  console.log(vehicleId,"hereeeeeeeee")
    if (!vehicleId || !organizationId) {
      console.error("Missing vehicleId or organizationId");
      return null;
    }
    try {
      const response = await axios.get(
        `${config.baseURL}/vehicle/id?vehicleId=${vehicleId}&organizationId=${organizationId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.vehicle;
    } catch (error) {
      console.error(`Error fetching vehicle ${vehicleId}:`, error);
      return null;
    }
  };

  const getStarredAlertsData = async () => {
    try {
      if (!orgId) {
        console.warn(
          "Organization ID is missing. Skipping starred alerts fetch."
        );
        setStarredAlerts([]);
        return [];
      }
      const response = await getStarredAlerts(orgId);
      console.log("Starred alerts:", response);
      setStarredAlerts(response);
      return response;
    } catch (error) {
      console.error("Error fetching starred alerts:", error);
      setStarredAlerts([]);
      return [];
    }
  };
  const fetchAlertCounts = async () => {
    try {
      const counts = await getAlertCounts();
      setAlertCounts(counts);
    } catch (error) {
      console.error("Error fetching alert counts:", error);
      setAlertCounts({
        total: 0,
        byType: { USER: 0, VEHICLE: 0, EXPIRY: 0 },
      });
    }
  };
  const fetchOrganizationVehicles = async () => {
    try {
      const vehicles = await getAllOrganizationVehicles();
      setOrganizationVehicles(vehicles);

      // Create a names map using device_id as key (since alerts use vehicleId which matches device_id)
      const namesMap = {};
      vehicles.forEach((vehicle) => {
        namesMap[vehicle.device_id] =
          vehicle.name || vehicle.vehicleName || vehicle.device_id;
      });
      setVehicleNamesMap(namesMap);
    } catch (error) {
      console.error("Error fetching organization vehicles:", error);
    }
  };

  // useEffect(() => {
  //   if (vehicleAlerts.length > 0) {
  //     fetchVehicleNames();
  //   }
  // }, [vehicleAlerts]);

  useEffect(() => {
    // Fetch organization vehicles when component mounts
    fetchOrganizationVehicles();
  }, []);

  useEffect(() => {
    if (orgId) {
      getStarredAlertsData();
    }
  }, [orgId]);

  useEffect(() => {
    if (selectedTab >= mainTabs.length) {
      setSelectedTab(0);
    }
  }, [mainTabs.length, selectedTab, setSelectedTab]);

  // Open the tab requested by whoever navigated here, then drop the hint so a
  // manual tab change isn't undone on the next render.
  useEffect(() => {
    const requestedTabKey = location.state?.alertTabKey;
    if (!requestedTabKey) return;

    const requestedIndex = mainTabs.findIndex(
      (tab) => tab.tabKey === requestedTabKey,
    );
    if (requestedIndex >= 0) {
      setSelectedTab(requestedIndex);
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [location, mainTabs, navigate, setSelectedTab]);

  // Reset tab-specific filters when tab changes
  useEffect(() => {
    setTabFilterType("all");
    // setTabStartDate(null);
    // setTabEndDate(null);
    setTabSelectedUser("");
    setTabSelectedStatus("");
    setPersistentSelectedAlerts([]);
    setSelectedAlerts([]);
    setSelectAll(false);
    setTabSelectedVehicle([]);
  }, [selectedTab]);

  useEffect(() => {
    // Fetch counts immediately for tab badges
    fetchAlertCounts();
    // Fetch full alerts for display
    fetchAlerts(true);

    const intervalId = setInterval(() => {
      fetchAlertCounts();
      fetchAlerts(true);
    }, AUTO_FETCH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [selectedTab, tabStartDate, tabEndDate]);

  useEffect(() => {
    // Reset page and get filtered alerts
    setCurrentPage(1);
    const allAlerts = getVisibleAlerts();
    let newDisplayedAlerts;

    if (sortDirection === "desc") {
      // For newest first (desc) - show first PAGE_SIZE items
      newDisplayedAlerts = allAlerts.slice(0, listPageSize);
    } else {
      // For oldest first (asc) - show first listPageSize items (which are now the oldest)
      newDisplayedAlerts = allAlerts.slice(0, listPageSize);
    }

    setDisplayedAlerts(newDisplayedAlerts);

    setHasMore(allAlerts.length > listPageSize);

    // Maintain selection state based on persistent selections and filter type
    if (tabFilterType === "none") {
      setSelectedAlerts([]);
      setSelectAll(false);
      setPersistentSelectedAlerts([]);
    } else {
      const visibleAlertIds = allAlerts.map((alert) => alert.id);
      const validSelectedAlerts = persistentSelectedAlerts.filter((id) =>
        visibleAlertIds.includes(id)
      );
      setSelectedAlerts(validSelectedAlerts);
      const displayedAlertIds = newDisplayedAlerts.map((alert) => alert.id);
      const allDisplayedSelected =
        displayedAlertIds.length > 0 &&
        displayedAlertIds.every((id) => validSelectedAlerts.includes(id));
      setSelectAll(allDisplayedSelected);
    }
  }, [
    searchTerm,
    tabStartDate,
    tabEndDate,
    selectedTab,
    tabSelectedVehicle,
    tabSelectedUser,
    tabSelectedStatus,
    sortDirection,
    tabFilterType,
    persistentSelectedAlerts,
    listPageSize,
  ]);

  useEffect(() => {
    if (!lastAlertRef.current || isLoading || isFetchingMore || !hasMore)
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreAlerts();
      },
      { threshold: 0.1 }
    );
    observer.observe(lastAlertRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [lastAlertRef.current, isLoading, isFetchingMore, hasMore, currentPage]);

  const canLoadCategory = (category) => {
    if (category === "all") {
      return (
        hasAlertPermission("user") ||
        hasAlertPermission("vehicle") ||
        hasAlertPermission("expiry")
      );
    }
    return hasAlertPermission(category);
  };

  const fetchAlerts = async (reset = false) => {
    const category =
      ALERTS_DATA_CATEGORY_BY_TAB_KEY[mainTabs[selectedTab]?.tabKey];
    if (!category) return [];

    if (reset) {
      fetchGenerationRef.current += 1;
      paginationRef.current = { cursor: null, hasMore: false, loading: false };
    }

    const generation = fetchGenerationRef.current;
    const currentVisible = () =>
      getVisibleAlerts(readCategoryAlerts(alertsStoreRef.current, category));

    if (
      !reset &&
      (!paginationRef.current.hasMore || paginationRef.current.cursor == null)
    ) {
      return currentVisible();
    }
    if (paginationRef.current.loading) {
      return currentVisible();
    }

    paginationRef.current.loading = true;
    setIsLoading(reset);
    setIsFetchingMore(!reset);

    try {
      if (!canLoadCategory(category)) {
        if (generation === fetchGenerationRef.current) {
          commitAlertStore(emptyAlertStore());
          paginationRef.current = { cursor: null, hasMore: false, loading: false };
          setServerHasMore(false);
          setDisplayedAlerts([]);
          setHasMore(false);
        }
        return [];
      }

      const cursor = reset ? null : paginationRef.current.cursor;
      const dateRange =
        tabStartDate && tabEndDate ? getAlertDateQueryRange(tabStartDate, tabEndDate) : null;
      const [page, starred] = await Promise.all([
        fetchAlertsPage(category, cursor, undefined, dateRange),
        reset ? getStarredAlertsData() : Promise.resolve(starredAlerts),
      ]);
      if (generation !== fetchGenerationRef.current) return [];

      const starredIds = new Set(
        (Array.isArray(starred) ? starred : []).map((alert) => alert.id),
      );
      let incoming = (page.alerts || []).map((alert) =>
        withStarState(alert, starredIds),
      );
      if (category === "all") {
        incoming = incoming.filter((alert) => {
          if (alert.type === "USER") return hasAlertPermission("user");
          if (alert.type === "VEHICLE") return hasAlertPermission("vehicle");
          if (alert.type === "EXPIRY") return hasAlertPermission("expiry");
          return true;
        });
      }

      const currentStore = alertsStoreRef.current;
      const nextStore = { ...currentStore };
      const merged = reset
        ? incoming
        : mergeAlertsById(readCategoryAlerts(currentStore, category), incoming);

      if (category === "all") nextStore.all = merged;
      if (category === "user") nextStore.user = merged;
      if (category === "vehicle") nextStore.vehicle = merged;
      if (category === "expiry") nextStore.expiry = merged;

      commitAlertStore(nextStore);
      const hasNextPage = Boolean(page.hasMore && page.nextCursor != null);
      paginationRef.current = {
        cursor: page.nextCursor,
        hasMore: hasNextPage,
        loading: false,
      };
      setServerHasMore(hasNextPage);

      const visible = getVisibleAlerts(merged);
      if (reset) {
        setDisplayedAlerts(visible.slice(0, listPageSize));
        setCurrentPage(1);
        setHasMore(visible.length > listPageSize || hasNextPage);
      }

      return visible;
    } catch (err) {
      console.error("Error fetching alerts:", err);
      return currentVisible();
    } finally {
      if (generation === fetchGenerationRef.current) {
        paginationRef.current.loading = false;
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    }
  };

  const loadMoreAlerts = async () => {
    if (paginationRef.current.loading || isLoading || isFetchingMore) return;

    let allAlerts = getVisibleAlerts();
    const nextPage = currentPage + 1;
    const needed = nextPage * listPageSize;

    if (allAlerts.length < needed) {
      const canFetchMore =
        paginationRef.current.hasMore &&
        paginationRef.current.cursor != null &&
        (Boolean(tabStartDate && tabEndDate) || !isAlertListFiltered());

      if (!canFetchMore) {
        setHasMore(false);
        return;
      }

      const loadedCount = allAlerts.length;
      const loadedAlerts = await fetchAlerts(false);
      if (!Array.isArray(loadedAlerts) || loadedAlerts.length <= loadedCount) {
        setHasMore(false);
        return;
      }
      allAlerts = loadedAlerts;
    }

    if (allAlerts.length <= currentPage * listPageSize) {
      setHasMore(paginationRef.current.hasMore);
      return;
    }

    const endIndex = Math.min(needed, allAlerts.length);
    setDisplayedAlerts(allAlerts.slice(0, endIndex));
    setCurrentPage(nextPage);
    setHasMore(endIndex < allAlerts.length || paginationRef.current.hasMore);
    setCurrentPageStart(1);
    setCurrentPageEnd(endIndex);
  };

  const getVisibleAlerts = (sourceAlerts) => {
    let alerts = Array.isArray(sourceAlerts)
      ? [...sourceAlerts]
      : getAlertsByCategory();

    // Apply tab-specific filter type
    switch (tabFilterType) {
      case "unread":
        alerts = alerts.filter((alert) => !alert.isRead);
        break;
      case "read":
        alerts = alerts.filter((alert) => alert.isRead);
        break;
      case "starred": // NEW CASE
        alerts = alerts.filter((alert) => alert.isStarred);
        break;
      case "none":
        // setSelectedAlerts([]);
        // setSelectAll(false);
        // // alerts = [];
        break;
      case "all":
      default:
        break;
    }

    // if (searchTerm)
    //   alerts = alerts.filter((alert) =>
    //     alert.subject?.toLowerCase().includes(searchTerm.toLowerCase())
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      alerts = alerts.filter(
        (alert) =>
          alert.subject?.toLowerCase().includes(searchLower) ||
          alert.body?.toLowerCase().includes(searchLower)
      );
    }

    // Apply tab-specific date filters across the whole selected days.
    if (tabStartDate && tabEndDate) {
      const { start, end } = getAlertDateBounds(tabStartDate, tabEndDate);
      alerts = alerts.filter((alert) => {
        const createdAt = new Date(alert.createdAt);
        return createdAt >= start && createdAt <= end;
      });
    }

    // Apply tab-specific vehicle filter
    if (tabSelectedVehicle.length > 0) {
      alerts = alerts.filter((alert) =>
        tabSelectedVehicle.includes(alert.vehicleId)
      );
    }
    // Apply tab-specific user filter
    if (tabSelectedUser)
      alerts = alerts.filter((alert) => alert.userId === tabSelectedUser);

    // Apply tab-specific status filter
    if (tabSelectedStatus) {
      if (currentTabKey === "expiry") {
        // Check if it's a keyword-based filter
        const keyword = tabSelectedStatus.toLowerCase();
        if (["service", "insurance", "registration"].includes(keyword)) {
          alerts = alerts.filter(
            (alert) =>
              alert.status?.toLowerCase().includes(keyword) ||
              alert.subject?.toLowerCase().includes(keyword) ||
              alert.description?.toLowerCase().includes(keyword)
          );
        } else {
          // Exact match for full status names
          alerts = alerts.filter((alert) => alert.status === tabSelectedStatus);
        }
      } else {
        // For other tabs, use exact match
        alerts = alerts.filter((alert) => alert.status === tabSelectedStatus);
      }
    }
    return alerts.sort((a, b) =>
      sortDirection === "desc"
        ? new Date(b.createdAt) - new Date(a.createdAt)
        : new Date(a.createdAt) - new Date(b.createdAt)
    );
  };

  // const getAlertsByCategory = () => {
  //   switch (selectedTab) {
  //     case 0: return [...userAlerts, ...vehicleAlerts, ...expiryAlerts];
  //     case 1: return vehicleAlerts;
  //     case 2: return userAlerts;
  //     case 3: return expiryAlerts;
  //     default: return [];
  //   }
  // };

  const getAlertsByCategory = () => {
    const currentTab = mainTabs[selectedTab];
    if (!currentTab) return [];

    const category = ALERTS_DATA_CATEGORY_BY_TAB_KEY[currentTab.tabKey];

    switch (category) {
      case "all":
        return allTabAlerts;
      case "vehicle":
        return hasAlertPermission("vehicle") ? vehicleAlerts : [];
      case "user":
        return hasAlertPermission("user") ? userAlerts : [];
      case "expiry":
        return hasAlertPermission("expiry") ? expiryAlerts : [];
      default:
        return [];
    }
  };

  const getAlertIcon = (alert) => {
    if (alert.type === "VEHICLE")
      return <img src={VehicleIcon} alt="Vehicle" />;
    if (alert.type === "USER") return <img src={UsersIcon} alt="User" />;
    if (alert.type === "EXPIRY") return <img src={ExpiryIcon} alt="Expiry" />;
    return <NotificationsIcon color="primary" />;
  };

  // const getAllCount = () =>
  //   userAlerts.length + vehicleAlerts.length + expiryAlerts.length;
  // const getVehicleCount = () => vehicleAlerts.length;
  // const getUserCount = () => userAlerts.length;
  // const getExpiryCount = () => expiryAlerts.length;
  const getTabCount = (tab) => computeAlertMetric(tab.metricKey, alertCounts);

  const getFilteredAlertsCount = () => getReportedAlertTotal();
  const getFilteredSelectedAlertsCount = () => {
    if (tabFilterType === "none") return 0;
    return persistentSelectedAlerts.length;
  };

  const handleSelectAll = (event) => {
    // Prevent default behavior and stop propagation
    event.preventDefault();
    event.stopPropagation();

    // Allow selection even when filter type is "none"
    const checked = event.target.checked;
    setSelectAll(checked);

    if (checked) {
      // Select all visible alerts
      const newSelectedAlerts = displayedAlerts.map((alert) => alert.id);
      setSelectedAlerts(newSelectedAlerts);
      // Update persistent state with new selections
      const updatedPersistent = [
        ...new Set([...persistentSelectedAlerts, ...newSelectedAlerts]),
      ];
      setPersistentSelectedAlerts(updatedPersistent);
    } else {
      // Clear all selections
      setSelectedAlerts([]);
      // Remove displayed alerts from persistent state
      const displayedIds = displayedAlerts.map((alert) => alert.id);
      const updatedPersistent = persistentSelectedAlerts.filter(
        (id) => !displayedIds.includes(id)
      );
      setPersistentSelectedAlerts(updatedPersistent);
    }
  };

  const handleTabFilterChange = (event) => {
    const value = event.target.value;
    setTabFilterType(value);

    // When "none" is selected, immediately clear all selected alerts and selectAll
    if (value === "none") {
      setSelectedAlerts([]);
      setSelectAll(false);
      setPersistentSelectedAlerts([]);
    }

    // Reset other filters when changing filter type
    setTabSelectedVehicle([]);
    setTabSelectedUser("");
    setTabSelectedStatus("");
  };

  const handleNoneClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Always clear selections when "none" is clicked
    setSelectedAlerts([]);
    setSelectAll(false);

    // Ensure the filter is set to "none"
    setTabFilterType("none");

    // Clear persistent selections as well
    setPersistentSelectedAlerts([]);

    // Reset other filters
    setTabSelectedVehicle([]);
    setTabSelectedUser("");
    setTabSelectedStatus("");
  };

  const handleClick = (e) => {
    if (tabFilterType == "none") {
      console.log("none alert clicked");
      setSelectedAlerts([]);
      setSelectAll(false);
      setTabSelectedVehicle([]);
      setTabSelectedUser("");
      setTabSelectedStatus("");
    }
  };

  const handleNextPage = async () => {
    const { canGoNext } = getPaginationInfo();
    if (!canGoNext || isLoading || isFetchingMore) return;

    const newPage = currentPage + 1;
    const needed = newPage * listPageSize;
    let allAlerts = getVisibleAlerts();

    if (
      allAlerts.length < needed &&
      paginationRef.current.hasMore &&
      (Boolean(tabStartDate && tabEndDate) || !isAlertListFiltered())
    ) {
      allAlerts = await fetchAlerts(false);
      if (!Array.isArray(allAlerts)) return;
    }

    if (allAlerts.length <= currentPage * listPageSize) {
      setHasMore(false);
      return;
    }

    const endIndex = Math.min(needed, allAlerts.length);
    setCurrentPage(newPage);
    setDisplayedAlerts(allAlerts.slice(0, endIndex));
    const reportedTotal = isAlertListFiltered()
      ? allAlerts.length
      : getReportedAlertTotal();
    setHasMore(
      endIndex < reportedTotal &&
        (endIndex < allAlerts.length || paginationRef.current.hasMore),
    );
  };

  // Update your handlePreviousPage function:
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      const allAlerts = getVisibleAlerts();

      // For both desc and asc, we show fewer items from the beginning
      // because getVisibleAlerts() already sorts them correctly
      const endIndex = newPage * listPageSize;
      setDisplayedAlerts(allAlerts.slice(0, endIndex));

      const totalPages = Math.ceil(allAlerts.length / listPageSize);
      setHasMore(totalPages > newPage);
    }
  };

  const replaceStoredAlert = (alertId, updatedData) => {
    const mapAlert = (alerts) =>
      alerts.map((alert) => (alert.id === alertId ? updatedData : alert));
    const current = alertsStoreRef.current;
    commitAlertStore({
      all: mapAlert(current.all),
      user: mapAlert(current.user),
      vehicle: mapAlert(current.vehicle),
      expiry: mapAlert(current.expiry),
    });
    setDisplayedAlerts((prev) => mapAlert(prev));
  };

  const dropStoredAlert = (alertId) => {
    const drop = (alerts) => alerts.filter((alert) => alert.id !== alertId);
    const current = alertsStoreRef.current;
    commitAlertStore({
      all: drop(current.all),
      user: drop(current.user),
      vehicle: drop(current.vehicle),
      expiry: drop(current.expiry),
    });
    setDisplayedAlerts((prev) => drop(prev));
  };

  const findStoredAlert = (alertId) =>
    [
      ...alertsStoreRef.current.all,
      ...alertsStoreRef.current.user,
      ...alertsStoreRef.current.vehicle,
      ...alertsStoreRef.current.expiry,
    ].find((alert) => alert.id === alertId);

  const deleteAlert = async (alertId) => {
    try {
      await deleteAlertById(alertId);
      dropStoredAlert(alertId);
      setSelectedAlerts((prev) => prev.filter((id) => id !== alertId));
      setPersistentSelectedAlerts((prev) =>
        prev.filter((id) => id !== alertId)
      );

      await fetchAlertCounts(); // Refresh counts after delete
    } catch (err) {
      console.error("Error deleting alert:", err);
    }
  };

  const deleteMultipleAlerts = async () => {
    if (tabFilterType === "none" || selectedAlerts.length === 0) return;
    setIsLoading(true);
    for (const alertId of selectedAlerts) {
      await deleteAlert(alertId);
    }
    setIsLoading(false);
    setSelectedAlerts([]);
    setSelectAll(false);
    setPersistentSelectedAlerts([]);
  };

  const markAsRead = async () => {
    if (tabFilterType === "none" || selectedAlerts.length === 0) return;

    for (const alertId of selectedAlerts) {
      const alert = findStoredAlert(alertId);
      if (alert && !alert.isRead) {
        const updatedData = {
          ...alert,
          isRead: true,
          alertStatus: "read",
          statusFlag: "READ",
        };
        await updateAlert(updatedData);
        replaceStoredAlert(alertId, updatedData);
      }
    }
    setDisplayedAlerts((prev) =>
      prev.map((alert) => ({ ...alert, expanded: false }))
    );
    setPersistentSelectedAlerts([]);

    await fetchAlertCounts();
  };

  const markAsUnread = async () => {
    if (tabFilterType === "none" || selectedAlerts.length === 0) return;
    for (const alertId of selectedAlerts) {
      const alert = findStoredAlert(alertId);
      if (alert && alert.isRead) {
        const updatedData = {
          ...alert,
          isRead: false,
          alertStatus: "unRead",
          statusFlag: "UNREAD",
        };
        await updateAlert(updatedData);
        replaceStoredAlert(alertId, updatedData);
      }
    }
    setDisplayedAlerts((prev) =>
      prev.map((alert) => ({ ...alert, expanded: false }))
    );
    setPersistentSelectedAlerts([]);
    await fetchAlertCounts();
  };

  // Check if selected alerts have unread items
  const hasUnreadSelected = () => {
    if (selectedAlerts.length === 0 || tabFilterType === "none") return false;
    return selectedAlerts.some((id) => {
      const alert = findStoredAlert(id);
      return alert && !alert.isRead;
    });
  };

  // Check if selected alerts have read items
  const hasReadSelected = () => {
    if (selectedAlerts.length === 0 || tabFilterType === "none") return false;
    return selectedAlerts.some((id) => {
      const alert = findStoredAlert(id);
      return alert && alert.isRead;
    });
  };

  const handleSortToggle = () => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  // Determine which action to show based on selected alerts
  const getEnvelopeAction = () => {
    if (tabFilterType === "none") return null;

    const hasUnread = hasUnreadSelected();
    const hasRead = hasReadSelected();

    if (hasUnread && hasRead) {
      // If both read and unread are selected, prioritize marking as read
      return "markAsRead";
    } else if (hasUnread) {
      // If only unread are selected, show mark as read
      return "markAsRead";
    } else if (hasRead) {
      // If only read are selected, show mark as unread
      return "markAsUnread";
    }
    return null;
  };

  // Get unique vehicles and users for dropdowns
  const getUniqueVehicles = () => {
    return organizationVehicles.map((vehicle) => vehicle.device_id);
  };

  const handletoggleAlertStar = async (alertId, isStarred) => {
    console.log(
      "Attempting to toggle star for alertId:",
      alertId,
      "to isStarred:",
      isStarred
    );
    const alert = findStoredAlert(alertId);
    if (alert) {
      console.log("Alert found:", alert);
      const updatedData = {
        ...alert,
        isStarred: isStarred,
      };
      try {
        console.log("Calling toggleAlertStar API with data:", updatedData);
        await toggleAlertStar(updatedData);
        replaceStoredAlert(alertId, updatedData);
        if (isStarred) {
          setStarredAlerts((prev) => [...prev, updatedData]);
        } else {
          setStarredAlerts((prev) => prev.filter((a) => a.id !== alertId));
        }
        console.log("Star status updated successfully");
      } catch (error) {
        console.error("Error toggling star status:", error);
      }
    } else {
      console.warn("Alert not found for ID:", alertId);
    }
  };

  const getUniqueUsers = () => {
    const users = [
      ...new Set(userAlerts.map((alert) => alert.userId).filter(Boolean)),
    ];
    return users;
  };

  const getUniqueStatuses = () => {
    const currentAlerts = getAlertsByCategory();
    const statuses = [
      ...new Set(currentAlerts.map((alert) => alert.status).filter(Boolean)),
    ];
    return statuses;
  };

  const expiryCategory = [
    "Service Due Date",
    "Insurance Policy Expiry Date",
    "Re-Registration Due Date",
  ];

  const isFilterVisible = (widget) => {
    if (!widget.visibleOnTab) return true;
    return widget.visibleOnTab === currentTabKey;
  };

  const getFilterWidgetLabel = (widget, fallback = "") => {
    if (widget?.labelKey) {
      return t(widget.labelKey);
    }
    return fallback ? t(fallback) : "";
  };

  const getFilterOptionLabel = (option) => {
    return t(option?.labelKey || option?.value || "");
  };

  const getDefaultFilterOptionValue = (widget) => {
    const options = widget?.options || [];
    return options.length > 0 ? options[0].value : undefined;
  };

  const getFilterOptionDisplayLabel = (widget, option) => {
    if (widget?.labelKey && option?.value === getDefaultFilterOptionValue(widget)) {
      return getFilterWidgetLabel(widget);
    }
    return getFilterOptionLabel(option);
  };

  const getSelectedFilterLabel = (widget, value) => {
    const option = (widget.options || []).find((item) => item.value === value);
    if (!option) {
      return getFilterWidgetLabel(widget);
    }
    return getFilterOptionDisplayLabel(widget, option);
  };

  const renderFilterWidget = (widget) => {
    switch (widget.type) {
      case "dateRange":
        return (
          <CustomDateRangePicker
            key={widget.id}
            labelKey={widget.labelKey || "Select Date Range"}
            startDate={tabStartDate}
            endDate={tabEndDate}
            onStartDateChange={setTabStartDate}
            onEndDateChange={setTabEndDate}
          />
        );
      case "selectFilter":
        if (widget.filterKey === "alertStatus") {
          return (
            <StyledFormControl
              key={widget.id}
              sx={{
                minWidth: isSmallScreen ? "100px" : "150px",
                fontSize: "16px",
                color: "#1F242E",
              }}
            >
              <StyledSelect
                labelId="tab-filter-label"
                value={tabFilterType}
                onChange={handleTabFilterChange}
                renderValue={(value) => getSelectedFilterLabel(widget, value)}
              >
                {(widget.options || []).map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    onClick={option.value === "none" ? handleNoneClick : undefined}
                  >
                    {getFilterOptionDisplayLabel(widget, option)}
                  </MenuItem>
                ))}
              </StyledSelect>
            </StyledFormControl>
          );
        }
        if (widget.filterKey === "expiryType") {
          return (
            <StyledFormControl
              key={widget.id}
              sx={{ minWidth: isSmallScreen ? "100px" : "150px" }}
            >
              <StyledSelect
                labelId="expiry-filter-label"
                value={tabSelectedStatus}
                onChange={(e) => setTabSelectedStatus(e.target.value)}
                displayEmpty
                renderValue={(value) => getSelectedFilterLabel(widget, value)}
              >
                {(widget.options || []).map((option) => (
                  <MenuItem key={option.value || "all"} value={option.value}>
                    {getFilterOptionDisplayLabel(widget, option)}
                  </MenuItem>
                ))}
              </StyledSelect>
            </StyledFormControl>
          );
        }
        return null;
      case "multiSelectFilter":
        if (widget.filterKey !== "vehicle") return null;
        return (
          <StyledFormControl
            key={widget.id}
            sx={{ minWidth: isSmallScreen ? "100px" : "150px" }}
          >
            <StyledSelect
              labelId="vehicle-filter-label"
              multiple
              value={tabSelectedVehicle}
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes("")) {
                  setTabSelectedVehicle([]);
                } else {
                  setTabSelectedVehicle(value);
                }
              }}
              displayEmpty
              renderValue={(selected) => {
                const emptyLabel = getFilterWidgetLabel(widget, "All Vehicles");
                if (selected.length === 0) {
                  return emptyLabel;
                }
                if (selected.length === 1) {
                  return vehicleNamesMap[selected[0]] || selected[0];
                }
                const availableVehicles = getUniqueVehicles();
                if (selected.length === availableVehicles.length) {
                  return emptyLabel;
                }
                return `${selected.length} vehicles selected`;
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            >
              <MenuItem value="">
                <Checkbox
                  checked={
                    tabSelectedVehicle.length === getUniqueVehicles().length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setTabSelectedVehicle(getUniqueVehicles());
                    } else {
                      setTabSelectedVehicle([]);
                    }
                  }}
                  size="small"
                  sx={{
                    color: "#20A8E0",
                    "&.Mui-checked": {
                      color: "#20A8E0",
                    },
                    marginRight: "8px",
                  }}
                />
                <ListItemText
                  primary={getFilterWidgetLabel(widget, "All Vehicles")}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "14px",
                      fontWeight:
                        tabSelectedVehicle.length === getUniqueVehicles().length
                          ? 600
                          : 400,
                    },
                  }}
                />
              </MenuItem>
              {getUniqueVehicles().map((deviceId) => (
                <MenuItem key={deviceId} value={deviceId}>
                  <Checkbox
                    checked={tabSelectedVehicle.includes(deviceId)}
                    size="small"
                    sx={{
                      color: "#20A8E0",
                      "&.Mui-checked": {
                        color: "#20A8E0",
                      },
                      marginRight: "8px",
                    }}
                  />
                  <ListItemText
                    primary={vehicleNamesMap[deviceId] || deviceId}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontSize: "14px",
                        fontWeight: tabSelectedVehicle.includes(deviceId)
                          ? 600
                          : 400,
                      },
                    }}
                  />
                </MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>
        );
      default:
        return null;
    }
  };

  const renderTabSpecificFilters = () => {
    return (
      <Card
        variant="none"
        sx={{ borderRadius: "8px", mb: 2, padding: "16px 0px" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            gap: 2,
            alignItems: isSmallScreen ? "stretch" : "center",
            flexWrap: "wrap",
          }}
        >
          {filterWidgets.filter(isFilterVisible).map(renderFilterWidget)}
        </Box>
      </Card>
    );
  };
  const envelopeAction = getEnvelopeAction();
  const isNoneFilter = tabFilterType === "none";
  const hasSelectedAlerts = selectedAlerts.length > 0 && !isNoneFilter;
  const listTitleWidget = getListHeaderWidget("listTitle");
  const searchWidget = getListHeaderWidget("search");
  const sortWidget = getListHeaderWidget("sort");
  const sortOptions = sortWidget?.options || [
    { value: "desc", labelKey: "Newest" },
    { value: "asc", labelKey: "Oldest" },
  ];
  const bulkActionsWidget = getListHeaderWidget("bulkActions");
  const enabledBulkActions = useMemo(
    () => getBulkActionsForWidget(bulkActionsWidget, state?.features),
    [bulkActionsWidget, state?.features],
  );
  const hasBulkAction = (actionKey) =>
    enabledBulkActions.some((action) => action.actionKey === actionKey);
  const getBulkAction = (actionKey) =>
    enabledBulkActions.find((action) => action.actionKey === actionKey);

  const tooltipSx = {
    backgroundColor: "white",
    color: "black",
    padding: "8px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    whiteSpace: "nowrap",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    border: "1px solid #A5DCF3",
    zIndex: 10000,
    marginTop: "5px",
  };

  return (
    <>
      <Card variant="none" sx={{ borderRadius: "8px", mb: 2, padding: "2px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isSmallScreen ? "stretch" : "center",
            borderRadius: "8px",
          }}
        >
          <StyledTabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            variant={isXsScreen ? "scrollable" : "standard"}
            scrollButtons={isXsScreen ? "auto" : false}
            allowScrollButtonsMobile={isXsScreen}
            sx={{
              width: isSmallScreen ? "100%" : "100%",
              display: "flex",
              alignItems: "center",
              padding: "2px",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                minWidth: isXsScreen ? 80 : 100,
                flex: isXsScreen ? "none" : 1,
                padding: isXsScreen ? "6px 8px" : "12px 16px",
                fontSize: isXsScreen ? "0.75rem" : "inherit",
              },
              "& .Mui-selected": {
                backgroundColor: "white",
                boxShadow: "rgba(0, 0, 0, 0.24) 4px 3px 8px",
                zIndex: 2,
                position: "relative",
                // border: "0.5px solid grey",
                // borderRadius: "8px 8px 0px 0px",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#20A8E0",
                height: "3px",
                zIndex: "2",
              },
            }}
          >
            {mainTabs.map((tab) => (
              <Tab
                key={tab.tabKey}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexWrap: isXsScreen ? "wrap" : "nowrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isXsScreen ? "0.75rem" : "1.25rem",
                        fontWeight: "bold",
                        color: "#183059",
                      }}
                    >
                      {tab.label}
                    </Typography>
                    <Box
                      sx={{
                        ml: isXsScreen ? 0.5 : 1,
                        backgroundColor: "#B7BFCC",
                        borderRadius: "8px",
                        px: isXsScreen ? 0.8 : 1.5,
                        py: 0.2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: isXsScreen ? "0.65rem" : "1rem",
                          color: "#183059",
                        }}
                      >
                        {getTabCount(tab)}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            ))}
          </StyledTabs>
        </Box>
      </Card>
      <Paper
        elevation={8}
        sx={{
          p: 2,
          borderRadius: 2,
          border: "1px solid #A5DCF3",
          position: "relative",
        }}
      >
        {/* Tab-specific filters rendered under the tabs */}
        {renderTabSpecificFilters()}

        <Card sx={{ borderRadius: "8px", boxShadow: "none" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 16px",
              borderBottom: "1px solid #B7BFCC",
              backgroundColor: "#647490",
              borderRadius: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "0px 13px",
              }}
            >
              {hasListHeaderWidget("bulkSelect") && (
              <Checkbox
                size="small"
                checked={!isNoneFilter && selectAll}
                onChange={handleSelectAll}
                // disabled={isNoneFilter}
                sx={{
                  pl: 0,
                  color: "white",
                  "&.Mui-checked": { color: "white" },
                  "&.Mui-disabled": {
                    color: "rgba(255, 255, 255, 0.3)",
                    "& .MuiSvgIcon-root": {
                      color: "rgba(255, 255, 255, 0.3)",
                    },
                  },
                }}
              />
              )}
              {hasListHeaderWidget("listTitle") && (
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#FFFFFF" }}
              >
                {t(listTitleWidget?.labelKey || "Alerts")}
                {listTitleWidget?.showCount !== false
                  ? ` (${getFilteredAlertsCount()})`
                  : ""}
              </Typography>
              )}
              {/* Selected Item Count - only show when alerts are selected and not "none" filter */}

              {hasListHeaderWidget("bulkActions") &&
                hasSelectedAlerts &&
                (hasBulkAction("clearSelection") || hasBulkAction("selectedCount")) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginLeft: "16px",
                  }}
                >
                  {hasBulkAction("clearSelection") && (
                  <Tooltip
                    title={getBulkActionLabel(getBulkAction("clearSelection"), t)}
                    componentsProps={{
                      tooltip: {
                        sx: tooltipSx,
                      },
                    }}
                    placement="bottom"
                  >
                    <IconButton
                      onClick={() => {
                        setSelectedAlerts([]);
                        setSelectAll(false);
                        setPersistentSelectedAlerts([]);
                      }}
                      sx={{
                        color: "#FFFFFF",
                        padding: "4px",
                        "&:hover": {
                          color: "#f44336",
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                      }}
                      title={getBulkActionLabel(getBulkAction("clearSelection"), t)}
                    >
                      <ClearIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </Tooltip>
                  )}
                  {hasBulkAction("selectedCount") && (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "#FFFFFF",
                      marginRight: "10px",
                    }}
                  >
                    {persistentSelectedAlerts.length > 0 &&
                      !isNoneFilter &&
                      `${getFilteredSelectedAlertsCount()} `}
                    {getBulkActionLabel(getBulkAction("selectedCount"), t)}
                  </Typography>
                  )}
                </Box>
              )}

              {hasListHeaderWidget("bulkActions") &&
                hasSelectedAlerts &&
                hasBulkAction("delete") && (
                <Tooltip
                  title={getBulkActionLabel(getBulkAction("delete"), t)}
                  componentsProps={{
                    tooltip: {
                      sx: tooltipSx,
                    },
                  }}
                  placement="bottom"
                >
                  <IconButton
                    onClick={() => {
                      deleteMultipleAlerts();
                    }}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "50%",
                      padding: "4px",
                      width: "30px",
                      height: "30px",
                      ml: 1,
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <img
                      src={deleteIcon}
                      style={{
                        color: "#f44336",
                        width: "26px",
                        height: "26px",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              )}

              {hasListHeaderWidget("bulkActions") &&
                hasSelectedAlerts &&
                envelopeAction === "markAsRead" &&
                hasBulkAction("markAsRead") && (
                <Tooltip
                  title={getBulkActionLabel(getBulkAction("markAsRead"), t)}
                  componentsProps={{
                    tooltip: {
                      sx: tooltipSx,
                    },
                  }}
                  placement="bottom"
                >
                  <IconButton
                    size="small"
                    onClick={markAsRead}
                    sx={{ color: "white", ml: 1 }}
                    title={getBulkActionLabel(getBulkAction("markAsRead"), t)}
                  >
                    <img
                      src={Envelop}
                      style={{
                        color: "#20A8E0",
                        background: "white",
                        borderRadius: "50%",
                        padding: "4px",
                        width: "32px",
                        height: "32px",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              )}

              {hasListHeaderWidget("bulkActions") &&
                hasSelectedAlerts &&
                envelopeAction === "markAsUnread" &&
                hasBulkAction("markAsUnread") && (
                <Tooltip
                  title={getBulkActionLabel(getBulkAction("markAsUnread"), t)}
                  componentsProps={{
                    tooltip: {
                      sx: tooltipSx,
                    },
                  }}
                  placement="bottom"
                >
                  <IconButton
                    size="small"
                    onClick={markAsUnread}
                    sx={{ color: "white", ml: 1 }}
                    title={getBulkActionLabel(getBulkAction("markAsUnread"), t)}
                  >
                    <MailIcon
                      sx={{
                        color: "#20A8E0",
                        background: "white",
                        borderRadius: "50%",
                        padding: "4px",
                        width: "36px",
                        height: "36px",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {hasListHeaderWidget("search") && (
            <TextField
              placeholder={t(searchWidget?.placeholderKey || "search")}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: searchTerm && (
                  <InputAdornment position="start" sx={{ marginLeft: "8px" }}>
                    <Tooltip
                      title={t("Clear Search")}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "white",
                            color: "black",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                            border: "1px solid #A5DCF3",
                            zIndex: 10000,
                            marginTop: "5px",
                          },
                        },
                      }}
                      placement="bottom"
                    >
                      <IconButton
                        edge="start"
                        sx={{ padding: "4px", marginRight: "4px" }}
                        onClick={() => setSearchTerm("")}
                      >
                        <ClearIcon sx={{ fontSize: "18px", color: "#666" }} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ marginRight: "8px" }}>
                    <IconButton edge="end" sx={{ padding: "4px" }}>
                      <SearchIcon sx={{ fontSize: "20px", color: "#1F242E" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: isXsScreen ? "60%" : 200,
                minWidth: isXsScreen ? "150px" : "200px",
                "& .MuiInputBase-root": {
                  height: "35px",
                  border: "1px solid #414958",
                  borderRadius: "4px",
                  background: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 14px",
                },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiInputBase-input": {
                  color: "#1F242E",
                  padding: "8px 0",
                  lineHeight: "1.5",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#1F242E",
                  opacity: 0.7,
                },
              }}
            />
            )}
            {(hasListHeaderWidget("pagination") || hasListHeaderWidget("sort")) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginRight: "20px",
              }}
            >
              {hasListHeaderWidget("pagination") && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Previous Page Button */}
                <Tooltip
                  title={"Previous Page"}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "white",
                        color: "black",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        border: "1px solid #A5DCF3",
                        zIndex: 10000,
                        marginTop: "5px",
                      },
                    },
                  }}
                  placement="bottom"
                >
                  <IconButton
                    onClick={
                      sortDirection === "asc"
                        ? handleNextPage
                        : handlePreviousPage
                    }
                    disabled={(() => {
                      const { canGoPrevious, canGoNext } = getPaginationInfo();
                      return sortDirection === "asc"
                        ? !canGoNext
                        : !canGoPrevious;
                    })()}
                    sx={{
                      color: "white",
                      padding: "6px",
                      opacity: (() => {
                        const { canGoPrevious, canGoNext } =
                          getPaginationInfo();
                        const canNavigate =
                          sortDirection === "asc" ? canGoNext : canGoPrevious;
                        return !canNavigate ? 0.4 : 1;
                      })(),
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                      "&.Mui-disabled": {
                        color: "rgba(255, 255, 255, 0.4)",
                      },
                    }}
                  >
                    <ArrowBackIosIcon sx={{ fontSize: "16px" }} />
                  </IconButton>
                </Tooltip>

                {/* Pagination Info - Clickable for Sort Dropdown */}
                <Box
                  onClick={(event) => {
                    if (!hasListHeaderWidget("sort")) return;
                    setSortAnchorEl(event.currentTarget);
                    setSortDropdownOpen(true);
                  }}
                  sx={{
                    color: "white",
                    fontWeight: 500,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    cursor: hasListHeaderWidget("sort") ? "pointer" : "default",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      backgroundColor: hasListHeaderWidget("sort")
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "inherit", fontWeight: "inherit" }}
                  >
                    {(() => {
                      const { startIndex, endIndex, totalAlerts } =
                        getPaginationInfo();
                      return `${startIndex}-${endIndex} of ${totalAlerts}`;
                    })()}
                  </Typography>
                  <ArrowDropDownIcon
                    sx={{ color: "white", fontSize: "20px" }}
                  />
                </Box>

                {/* Next Page Button */}
                <Tooltip
                  title={"Next Page"}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "white",
                        color: "black",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        border: "1px solid #A5DCF3",
                        zIndex: 10000,
                        marginTop: "5px",
                      },
                    },
                  }}
                  placement="bottom"
                >
                  <IconButton
                    onClick={
                      sortDirection === "asc"
                        ? handlePreviousPage
                        : handleNextPage
                    }
                    disabled={(() => {
                      const { canGoNext, canGoPrevious } = getPaginationInfo();
                      return sortDirection === "asc"
                        ? !canGoPrevious
                        : !canGoNext;
                    })()}
                    sx={{
                      color: "white",
                      padding: "6px",
                      opacity: (() => {
                        const { canGoNext, canGoPrevious } =
                          getPaginationInfo();
                        const canNavigate =
                          sortDirection === "asc" ? canGoPrevious : canGoNext;
                        return !canNavigate ? 0.4 : 1;
                      })(),
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                      "&.Mui-disabled": {
                        color: "rgba(255, 255, 255, 0.4)",
                      },
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: "16px" }} />
                  </IconButton>
                </Tooltip>
              </Box>
              )}

              {hasListHeaderWidget("sort") && (
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#FFFFFF", ml: 2 }}
              >
                {t(sortWidget?.labelKey || "timestamp")}
              </Typography>
              )}
            </Box>
            )}
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : displayedAlerts.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <Typography variant="h6" color="textSecondary">
                {t("No alerts found")}
              </Typography>
            </Box>
          ) : (
            <Box>
              {displayedAlerts.map((alert, index) => (
                <Box
                  key={alert.id}
                  ref={
                    index === displayedAlerts.length - 1 ? lastAlertRef : null
                  }
                  sx={{ marginBottom: "8px", marginTop: "8px" }}
                >
                  <AlertItem
                    alert={alert}
                    icon={getAlertIcon(alert)}
                    // isSelected={tabFilterType === "none" ? false : selectedAlerts.includes(alert.id)} // Force unselected when "none"
                    isSelected={selectedAlerts.includes(alert.id)} // Force unselected when "none"
                    onSelect={(alertId, isSelected) => {
                      // Prevent selection when filter type is "none"
                      // if (tabFilterType === "none") {
                      //   return;
                      // }
                      if (isSelected) {
                        const newSelectedAlerts = [...selectedAlerts, alertId];
                        setSelectedAlerts(newSelectedAlerts);
                        setPersistentSelectedAlerts((prev) => [
                          ...new Set([...prev, alertId]),
                        ]);
                        if (
                          newSelectedAlerts.length === displayedAlerts.length &&
                          displayedAlerts.every((alert) =>
                            newSelectedAlerts.includes(alert.id)
                          )
                        ) {
                          setSelectAll(true);
                        }
                      } else {
                        setSelectedAlerts((prev) =>
                          prev.filter((id) => id !== alertId)
                        );
                        setPersistentSelectedAlerts((prev) =>
                          prev.filter((id) => id !== alertId)
                        );
                        setSelectAll(false);
                      }
                    }}
                    onDelete={deleteAlert}
                    onToggleStar={handletoggleAlertStar}
                    onMarkAsRead={async (alertId) => {
                      const alert = findStoredAlert(alertId);
                      if (alert && !alert.isRead) {
                        const updatedData = {
                          ...alert,
                          isRead: true,
                          alertStatus: "read",
                          statusFlag: "READ",
                        };
                        await updateAlert(updatedData);
                        replaceStoredAlert(alertId, updatedData);
                      }
                    }}
                    onMarkAsUnread={async (alertId) => {
                      const alert = findStoredAlert(alertId);
                      if (alert && alert.isRead) {
                        const updatedData = {
                          ...alert,
                          isRead: false,
                          alertStatus: "unRead",
                          statusFlag: "UNREAD",
                        };
                        await updateAlert(updatedData);
                        replaceStoredAlert(alertId, updatedData);
                      }
                    }}
                  />
                </Box>
              ))}

              {isFetchingMore && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          )}
        </Card>

        {hasListHeaderWidget("sort") && (
        <Menu
          anchorEl={sortAnchorEl}
          open={sortDropdownOpen}
          onClose={() => {
            setSortDropdownOpen(false);
            setSortAnchorEl(null);
          }}
          PaperProps={{
            sx: {
              mt: 1,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
              border: "1px solid #A5DCF3",
              borderRadius: "6px",
              "& .MuiMenuItem-root": {
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#183059",
                "&:hover": {
                  backgroundColor: "rgba(32, 168, 224, 0.1)",
                },
              },
            },
          }}
        >
          {sortOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              setSortDirection(option.value);
              setSortDropdownOpen(false);
              setSortAnchorEl(null);
            }}
            sx={{
              fontWeight: 500,
              backgroundColor:
                sortDirection === option.value ? "#e6e6e7ff" : "transparent",
              color: sortDirection === option.value ? "white" : "#183059",
              "&:hover": {
                backgroundColor:
                  sortDirection === option.value
                    ? "#183059"
                    : "rgba(32, 168, 224, 0.1)",
              },
            }}
          >
            {t(option.labelKey)}
          </MenuItem>
          ))}
        </Menu>
        )}
      </Paper>
    </>
  );
};

export default AlertList;
